/**
 * Phase 20 wizard — Step 1: project intent.
 *
 * Renders the server-provided intent cards (the same `intent_picker`
 * payload Stella emits on a fresh chat) as tappable choices. On a brand-
 * new estimate that has no cards yet, falls back to a labelled prompt
 * that sends the customer's first message — so the customer is never
 * staring at a bare chat box.
 */

import { useState, type FormEvent } from "react";

import type { OptionsCardPayload } from "@starz/shared/api/types.gen";
import { OptionsCardGrid } from "@/components/chat/OptionsCardGrid";

interface Props {
  /** The latest `intent_picker` options payload, if Stella has emitted one. */
  payload: OptionsCardPayload | null;
  /** Fire the same synthetic turn the inline intent picker fires. */
  onSelectIntent: (intentId: string, displayName: string) => void;
  /** Free-text fallback when no cards exist yet (empty estimate). */
  onSendText: (text: string) => void;
  disabled?: boolean;
}

export function IntentStep({ payload, onSelectIntent, onSendText, disabled = false }: Props) {
  const [draft, setDraft] = useState("");
  const hasCards = payload != null && payload.cards.length > 0;

  function submitText(e: FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || disabled) return;
    setDraft("");
    onSendText(text);
  }

  if (hasCards) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-warm-gray">
          Tap the space you'd like to remodel to get started.
        </p>
        <OptionsCardGrid
          payload={payload}
          selectedOptionId={null}
          disabled={disabled}
          onSelect={(card) => onSelectIntent(card.option_id, card.display_name)}
        />
      </div>
    );
  }

  return (
    <form onSubmit={submitText} className="space-y-4">
      <label htmlFor="wizard-intent" className="block text-sm text-warm-gray">
        Tell Stella what you're remodeling — for example, "my main bathroom" or "the kitchen
        cabinets".
      </label>
      <textarea
        id="wizard-intent"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={disabled}
        rows={2}
        placeholder="What space are you thinking about?"
        className="w-full resize-none rounded-lg border border-starz-gold/20 bg-white px-4 py-3 text-base text-starz-charcoal shadow-inner focus:border-starz-gold focus:outline-none focus:ring-2 focus:ring-starz-gold/40 disabled:opacity-60"
      />
      <button
        type="submit"
        disabled={disabled || !draft.trim()}
        className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-off-white transition-opacity hover:bg-starz-navy/90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Continue
      </button>
    </form>
  );
}
